import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faUserShield, faClock, faPercent
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/CustomerOnboarding-Process.png";
import vis from "../../assets/images/use-cases/CustomerOnboarding-Dashboard.png";

const CustomerOnboardingOptimisation = () => {
    return (
        <>
            <SEO
                title="Customer Onboarding Optimisation: AI Digital Twin for KYC | Nebulyx AI"
                description="Simulate changes to your KYC and onboarding process to reduce drop-off and time-to-activate while maintaining compliance. Test step reordering and automation rules offline."
                keywords="AI digital twin, KYC optimisation, customer onboarding, compliance automation, drop-off reduction, fintech simulation"
                canonicalUrl="https://nebulyx.ai/use-cases/customer-onboarding-optimisation"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faUserShield}/>
                                            &nbsp;
                                            Financial Services
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;How do we reduce onboarding drop-off while staying compliant?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Simulate changes to your KYC and onboarding process offline to test step reordering, automation rules, and parallel processing, before deploying to production.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faClock} className="stat-icon"/>
                                <span className="stat-value">5→2 days</span>
                                <span className="stat-label">Time-to-Activate</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faPercent} className="stat-icon"/>
                                <span className="stat-value">25%</span>
                                <span className="stat-label">Drop-off Reduction</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">100%</span>
                                <span className="stat-label">Compliance Maintained</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>High drop-off rates during onboarding journey</li>
                                            <li>Sequential verification steps create bottlenecks</li>
                                            <li>No way to test process changes without production risk</li>
                                            <li>Compliance concerns block innovation</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Model onboarding as connected process steps</li>
                                            <li>Overlay drop-off and bottleneck analytics</li>
                                            <li>Simulate process changes offline</li>
                                            <li>Generate optimised flow recommendations</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Test process changes without compliance risk</li>
                                            <li>Identify parallel processing opportunities</li>
                                            <li>Data-driven automation thresholds</li>
                                            <li>Predict impact before deployment</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect onboarding data, model each verification step, overlay analytics, then simulate process changes to find the optimal flow configuration.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>CRM applications, ID verification results, credit checks, compliance flags, activation logs</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Model the Process</strong>
                                    <span>Map each onboarding step, dependencies, handoffs, and decision points</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Overlay Analytics</strong>
                                    <span>Drop-off rates, bottleneck detection, compliance risk scores, time-to-activate</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Simulate step reordering, automation rules, parallel processing options</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Connects CRM, identity verification, credit, compliance, and core banking systems to model the end-to-end onboarding journey.
                                            Each step captures pass/fail rates, time-at-stage, and drop-off points. Enables offline simulation of process changes
                                            so teams can optimise flow without risking compliance or customer experience.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>Onboarding Simulation Workbench</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises the onboarding funnel with drop-off rates and bottlenecks at each step. Allows teams to drag-and-drop
                                            step reordering, configure automation thresholds, and enable parallel processing, then see predicted impact on
                                            time-to-activate and conversion rates before committing changes.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Customer Applications (CRM)</li>
                                            <li>ID Verification Results</li>
                                            <li>Credit Check Scores</li>
                                            <li>PEP/Sanctions Flags</li>
                                            <li>Compliance Risk Ratings</li>
                                            <li>Account Activation Timestamps</li>
                                            <li>Drop-off Event Logs</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Drop-off probability prediction</li>
                                            <li>Bottleneck detection</li>
                                            <li>Compliance risk scoring</li>
                                            <li>Time-to-activate estimator</li>
                                            <li>Automation threshold optimizer</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Reorder verification steps</li>
                                            <li>Auto-approve low-risk applications</li>
                                            <li>Parallel ID + credit checks</li>
                                            <li>Adjust manual review thresholds</li>
                                            <li>Add document pre-validation</li>
                                            <li>Change compliance escalation rules</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling the onboarding process and simulating thousands of flow variations offline,
                                        teams could discover that running ID verification and credit checks in parallel-combined with
                                        auto-approving low-risk applications below a defined threshold
                                        <strong> could reduce time-to-activate from 5 days to 2 days</strong>.
                                        Simulation may reveal that 60% of drop-offs occur during document upload wait times,
                                        an insight that enables targeted intervention without compromising compliance.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">Parallel + Auto-Approve</span>
                                        <span className="discovery-note">5→2 day activation, tested offline</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Want to Optimise Onboarding Without Compliance Risk?</h2>
                            <p>
                                If drop-off rates are costing you customers and process changes feel too risky,
                                let's build your Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default CustomerOnboardingOptimisation;